import * as React from 'react';
import {Query} from 'react-apollo';

import gql from 'graphql-tag';
import moment from 'moment';

import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';
import {isFirstLoad} from 'nutshell-core/utils/graphql-loading';

import type {
    LastContactedTimesPeep as LastContactedTimesPeepFragment,
    GetLastContactedTimes as GetLastContactedTimesQuery,
    GetLastContactedTimesVariables as GetLastContactedTimesQueryVariables,
} from 'nutshell-graphql-types';

import styles from './peep-last-contacted-times.module.css';

type OwnProps = {
    // Our backbone model doesn't initialize immediately, so we
    // need to pass a separate api-id to use for our fetching
    apiId: string;
    // Passing the backbone model so we know when to refetch
    // the times when it changes
    backboneModel: any;
};

type GraphQLPeepLastContactedTimesProps = {
    peep: LastContactedTimesPeepFragment | null | undefined;
    isLoading: boolean;
    errorMessage: string | null | undefined;
    onRefetchTimes: () => any;
};

type Props = OwnProps & GraphQLPeepLastContactedTimesProps;

/*
    Component that fetches and displays the last contacted time
    for a given user (and the last time anyone from their
    company contacted this peep).

    This is a separate component and fetch because it's sometimes
    over a second to fetch, and we don't want it to block anything
 */
export class PeepLastContactedTimesComponent extends React.Component<Props> {
    componentDidMount() {
        this.props.backboneModel.on('change', this.refetchTimes);
    }

    componentWillUnmount() {
        this.props.backboneModel.off('change', this.refetchTimes);
    }

    render() {
        if (this.props.errorMessage) {
            return (
                <div className={styles['peep-last-contacted-times']}>{this.props.errorMessage}</div>
            );
        }

        if (this.props.isLoading) {
            return null;
        }

        if (this.props.peep) {
            return (
                <div className={styles['peep-last-contacted-times']}>
                    {this.renderLastContactedTime(this.props.peep)}
                    {this.renderMyLastContactedTime(this.props.peep)}
                </div>
            );
        }

        return null;
    }

    renderLastContactedTime = (peep: LastContactedTimesPeepFragment) => {
        return peep.lastContactedTime ? (
            <p>Last contacted {moment.unix(peep.lastContactedTime).fromNow()}</p>
        ) : (
            <p>No one has contacted this {peep.__typename === 'Contact' ? 'person' : 'company'}</p>
        );
    };

    renderMyLastContactedTime = (peep: LastContactedTimesPeepFragment) => {
        return peep.myLastContactedTime ? (
            <p>Your last contact {moment.unix(peep.myLastContactedTime).fromNow()}</p>
        ) : (
            <p>
                You’ve never contacted this {peep.__typename === 'Contact' ? 'person' : 'company'}
            </p>
        );
    };

    refetchTimes = () => {
        this.props.onRefetchTimes();
    };
}

// One-off fragment specifically for this component
const PEEP_LAST_CONTACTED = gql`
    fragment LastContactedTimesPeep on Peep {
        id
        lastContactedTime
        myLastContactedTime
    }
`;

// Here's our actual graphQL mutation query code.
// Everything here is defined by the server, expect for any fragments
const GET_LAST_CONTACTED_TIMES = gql`
    query GetLastContactedTimes($id: ID!) {
        peep(id: $id) {
            ...LastContactedTimesPeep
        }
    }
    ${PEEP_LAST_CONTACTED}
`;

class LastContactedTimesQuery extends Query<
    GetLastContactedTimesQuery,
    GetLastContactedTimesQueryVariables
> {}

export const PeepLastContactedTimes = (props: OwnProps) => (
    <LastContactedTimesQuery query={GET_LAST_CONTACTED_TIMES} variables={{id: props.apiId}}>
        {({data, error, refetch, networkStatus}) => {
            const peep = data ? data.peep : null;

            return (
                <PeepLastContactedTimesComponent
                    {...props}
                    peep={peep}
                    isLoading={isFirstLoad(networkStatus)}
                    onRefetchTimes={refetch}
                    // @ts-expect-error
                    errorMessage={safelyGetGraphQLErrorMessage(error)}
                />
            );
        }}
    </LastContactedTimesQuery>
);
